import React from 'react';
import { supabase } from '@/lib/supabase.ts';
import toast from 'react-hot-toast';
import { useLoaderData } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { getWinnerStatus, submitVote } from '@/lib/server/voting';
import type { VotingPageData } from '@/lib/server/voting';
import type { VoterDetails } from '@/lib/types';
import {
  BeforeGameScreen,
  NoGameScreen,
  VoteForm,
  VotedCard,
  VotingClosedScreen,
  VotingScreen,
} from '../../components/voting/VoterScreens';

export default function Home() {
  const loaderData = useLoaderData({ from: '/' }) as VotingPageData;

  const [currentGame, setCurrentGame] = React.useState(loaderData.game);
  const [hasVoted, setHasVoted] = React.useState(!!loaderData.existingVote);
  const [userVote, setUserVote] = React.useState(loaderData.existingVote);
  const [isWinner, setIsWinner] = React.useState(loaderData.isWinner);
  const [selectedPlayer, setSelectedPlayer] = React.useState<string | null>(
    null,
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [voterDetails, setVoterDetails] = React.useState<VoterDetails>({});

  const handleVoterDetailChange = React.useCallback(
    (field: keyof VoterDetails, value: string) => {
      setVoterDetails((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const submitVoteFn = useServerFn(submitVote);

  const handlePlayerSelect = React.useCallback((id: string) => {
    setSelectedPlayer(id);
  }, []);

  // Read inside the realtime handler, which is only set up once per game
  const hasVotedRef = React.useRef(hasVoted);
  hasVotedRef.current = hasVoted;
  const winnersDrawnAtRef = React.useRef(loaderData.game?.winners_drawn_at ?? null);

  // Real-time subscriptions — client-side only, after hydration
  React.useEffect(() => {
    const gameId = loaderData.game?.id;
    const deviceId = loaderData.deviceId;

    // No subscriptions needed if there's no active game
    if (!gameId) return;

    const votesSubscription = supabase
      .channel(`user-votes-${deviceId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_votes',
          filter: `device_id=eq.${deviceId}`,
        },
        (payload) => {
          setHasVoted(true);
          setUserVote(
            payload.new as {
              player_id: string;
              player_name: string;
              player_number: number;
              player_image_url?: string;
            },
          );
        },
      )
      .subscribe();

    const gameSubscription = supabase
      .channel(`game-changes-${gameId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'games',
          filter: `id=eq.${gameId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          setCurrentGame((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              is_active: updated.is_active,
              voting_active: updated.voting_active,
            };
          });

          // The admin (re)drew winners: only voters can have won
          const drawnAt = updated.winners_drawn_at ?? null;
          if (drawnAt !== winnersDrawnAtRef.current) {
            winnersDrawnAtRef.current = drawnAt;
            if (hasVotedRef.current) {
              getWinnerStatus({ data: { gameId } })
                .then((status) => setIsWinner(status.isWinner))
                .catch(() => {});
            }
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(votesSubscription);
      supabase.removeChannel(gameSubscription);
    };
  }, [loaderData.deviceId, loaderData.game?.id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!currentGame?.id || !selectedPlayer) return;

    setSubmitting(true);

    try {
      const result = await submitVoteFn({
        data: {
          gameId: currentGame.id,
          playerId: selectedPlayer,
          details: loaderData.voterDetails ? voterDetails : undefined,
        },
      });

      if (!result.success) {
        if (result.error === 'details_required') {
          toast.error('Vul alle verplichte velden in');
        } else if (result.error === 'invalid_email') {
          toast.error('Vul een geldig e-mailadres in');
        } else if (result.error === 'invalid_phone') {
          toast.error('Vul een geldig telefoonnummer in');
        } else if (result.error === 'already_voted') {
          toast.error('Je hebt al gestemd voor deze wedstrijd');
        } else if (result.error === 'voting_closed') {
          toast.error('Stemmen is gesloten voor deze wedstrijd');
          setCurrentGame((prev) =>
            prev ? { ...prev, voting_active: false } : prev,
          );
        } else {
          toast.error('Kon stem niet registreren');
        }
        return;
      }

      if (result.vote) {
        setUserVote(result.vote);
        setHasVoted(true);
        toast.success('Bedankt voor je stem!');
      }
    } catch {
      toast.error('Er is een fout opgetreden');
    } finally {
      setSubmitting(false);
    }
  }

  const { texts, sponsor } = loaderData;

  if (!currentGame) {
    return <NoGameScreen texts={texts} />;
  }

  const now = new Date();
  const gameTime = React.useMemo(() => {
    const gameDate = new Date(currentGame.game_date);
    const [hours, minutes] = currentGame.game_time.split(':');
    gameDate.setHours(parseInt(hours), parseInt(minutes), 0);
    return gameDate;
  }, [currentGame.game_date, currentGame.game_time]);

  // Before game starts
  if (!currentGame?.voting_active && now < gameTime) {
    return (
      <BeforeGameScreen
        texts={texts}
        title={currentGame.title}
        gameTime={gameTime}
        sponsor={sponsor}
      />
    );
  }

  // After voting ends
  if (!currentGame.voting_active) {
    return (
      <VotingClosedScreen
        texts={texts}
        title={currentGame.title}
        sponsor={sponsor}
        isWinner={isWinner}
      />
    );
  }

  return (
    <VotingScreen
      texts={texts}
      title={currentGame.title}
      sponsor={sponsor}
      hasVoted={hasVoted}
    >
      {hasVoted ? (
        <VotedCard texts={texts} vote={userVote} isWinner={isWinner} />
      ) : (
        <VoteForm
          texts={texts}
          players={currentGame.players}
          selectedPlayer={selectedPlayer}
          onSelectPlayer={handlePlayerSelect}
          voterDetailsConfig={loaderData.voterDetails}
          voterDetails={voterDetails}
          onVoterDetailChange={handleVoterDetailChange}
          submitting={submitting}
          onSubmit={handleSubmit}
        />
      )}
    </VotingScreen>
  );
}
